// 카카오맵 JavaScript SDK 동적 로더
// 키는 /api/map.php 응답의 js_key 를 우선 사용하고, 실패 시 아래 대체 키를 사용합니다.
//
// SPA 주의점: index.html 에 script 를 넣으면 라우팅 시 초기화 타이밍이 어긋납니다.
// 반드시 autoload=false 로 로드한 뒤 kakao.maps.load() 콜백에서 지도를 생성해야 합니다.

export const KAKAO_JS_KEY_FALLBACK = "25646f96e67bbcdefe05f5985b7ee186";

export const KAKAO_LOAD_ERROR = "지도를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.";

export interface KakaoLatLng {
  getLat(): number;
  getLng(): number;
}

export interface KakaoLatLngBounds {
  getSouthWest(): KakaoLatLng;
  getNorthEast(): KakaoLatLng;
}

export interface KakaoMap {
  setCenter(latlng: KakaoLatLng): void;
  getCenter(): KakaoLatLng;
  panTo(latlng: KakaoLatLng): void;
  setLevel(level: number, opts?: { animate?: boolean; anchor?: KakaoLatLng }): void;
  getLevel(): number;
  setBounds(bounds: KakaoLatLngBounds): void;
  getBounds(): KakaoLatLngBounds;
  addControl(control: unknown, position?: string): void;
  relayout(): void;
}

export interface KakaoCustomOverlay {
  setMap(map: KakaoMap | null): void;
  setPosition(latlng: KakaoLatLng): void;
  setZIndex(z: number): void;
}

export interface KakaoMarker {
  setMap(map: KakaoMap | null): void;
}

export interface KakaoMarkerClusterer {
  addMarkers(markers: KakaoMarker[]): void;
  clear(): void;
}

export interface KakaoNamespace {
  maps: {
    LatLng: new (lat: number, lng: number) => KakaoLatLng;
    LatLngBounds: new (sw: KakaoLatLng, ne: KakaoLatLng) => KakaoLatLngBounds;
    Map: new (
      container: HTMLElement,
      opts: { center: KakaoLatLng; level: number }
    ) => KakaoMap;
    CustomOverlay: new (opts: {
      position: KakaoLatLng;
      content: string | HTMLElement;
      xAnchor?: number;
      yAnchor?: number;
      zIndex?: number;
      clickable?: boolean;
    }) => KakaoCustomOverlay;
    Marker: new (opts: { position: KakaoLatLng; map?: KakaoMap }) => KakaoMarker;
    MarkerClusterer: new (opts: {
      map: KakaoMap;
      averageCenter?: boolean;
      minLevel?: number;
      disableClickZoom?: boolean;
      markers?: KakaoMarker[];
    }) => KakaoMarkerClusterer;
    ZoomControl: new () => unknown;
    ControlPosition: {
      TOP: string;
      TOPLEFT: string;
      TOPRIGHT: string;
      LEFT: string;
      RIGHT: string;
      BOTTOM: string;
      BOTTOMLEFT: string;
      BOTTOMRIGHT: string;
    };
    event: {
      addListener(
        target: unknown,
        type: string,
        handler: (...args: unknown[]) => void
      ): void;
      removeListener(
        target: unknown,
        type: string,
        handler: (...args: unknown[]) => void
      ): void;
    };
    load(cb: () => void): void;
  };
}

declare global {
  interface Window {
    kakao?: KakaoNamespace;
  }
}

let sdkPromise: Promise<KakaoNamespace> | null = null;

function buildSdkUrl(appKey: string): string {
  return `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${appKey}&libraries=services,clusterer&autoload=false`;
}

export function loadKakaoSdk(appKey: string): Promise<KakaoNamespace> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error(KAKAO_LOAD_ERROR));
  }
  if (window.kakao?.maps?.Map) {
    return Promise.resolve(window.kakao);
  }
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise<KakaoNamespace>((resolve, reject) => {
    const fail = () => reject(new Error(KAKAO_LOAD_ERROR));

    // autoload=false 로 로드했으므로 반드시 kakao.maps.load() 를 호출해야 지도가 준비됩니다.
    const ready = () => {
      const kakao = window.kakao;
      if (!kakao?.maps) {
        fail();
        return;
      }
      if (typeof kakao.maps.load === "function") {
        kakao.maps.load(() => resolve(kakao));
      } else {
        resolve(kakao);
      }
    };

    const existing = document.querySelector<HTMLScriptElement>("script[data-kakao-sdk]");
    if (existing) {
      if (window.kakao?.maps) {
        ready();
        return;
      }
      existing.addEventListener("load", ready);
      existing.addEventListener("error", fail);
      return;
    }

    const script = document.createElement("script");
    script.src = buildSdkUrl(appKey);
    script.async = true;
    script.dataset.kakaoSdk = "true";
    script.addEventListener("load", ready);
    script.addEventListener("error", fail);
    document.head.appendChild(script);
  });

  return sdkPromise;
}